import type { AdminTicketActivityDto, AppTicketDto, TicketCommentDto } from "api/types";
import { AlertBar } from "components/AlertBar/AlertBar";
import { Breadcrumbs } from "components/Breadcrumbs/Breadcrumbs";
import { Button } from "components/Button/Button";
import { formatDate } from "components/FormattedDate/FormattedDate";
import { LoadingIcon } from "components/Icons/Icons";
import type { FormImage } from "components/ImageInput/useImageInput";
import { DocumentPaper } from "components/Paper/DocumentPaper";
import { Capture1, Capture2, Headline4, Overline1, Subtitle2 } from "components/Text/Text";
import { TicketResidentCommentField } from "components/Ticket/TicketResidentCommentField";
import { TicketResidentRateModal } from "components/Ticket/TicketResidentRateModal";
import { TicketResidentRejectModal } from "components/Ticket/TicketResidentRejectModal";
import { TicketStatus } from "components/Ticket/TicketStatus";
import { useSessionUser } from "hooks/Network/useSessionUser";
import { useBool } from "hooks/useBool";
import { useOnIntersection } from "hooks/useOnIntersection";
import { useSlug } from "hooks/useSlug";
import { TicketActivitiesSection } from "modules/tickets/components/TicketActivitySection";
import { TicketContent } from "modules/tickets/components/TicketDetails/TicketContent";
import { useCallback, useMemo } from "react";
import { Calendar as CalendarIcon, Lock, Star as StarIcon, Users as UsersIcon } from "react-feather";
import { Trans, useTranslation } from "react-i18next";
import { routes } from "routes";

export interface LayoutProps {
  ticket: AppTicketDto;
  comments: TicketCommentDto[];
  isFetchingMoreComments: boolean;
  hasMoreComments: boolean;
  isRatingSubmitting: boolean;
  fetchMoreComments: () => unknown;
  onCreateComment: (payload: { content: string; image?: FormImage; internal?: boolean }) => Promise<unknown>;
  onRate: (payload: { rating?: number; remark: string; closingDate: Date }) => Promise<unknown>;
}

export function Layout({
  ticket,
  comments,
  isFetchingMoreComments,
  hasMoreComments,
  isRatingSubmitting,
  fetchMoreComments,
  onCreateComment,
  onRate,
}: LayoutProps): React.ReactNode {
  const slug = useSlug();
  const sessionUser = useSessionUser();
  const { i18n, t } = useTranslation();
  const [isRejectModalOpen, rejectModalOpenHandlers] = useBool(false);
  const [isRateModalOpen, rateModalOpenHandlers] = useBool(false);

  const ref = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      if (!isFetchingMoreComments && hasMoreComments) {
        fetchMoreComments();
      }
    }, [fetchMoreComments, hasMoreComments, isFetchingMoreComments]),
  });

  const activities = useMemo(() => {
    return comments.map(
      (comment) =>
        ({
          id: comment.id,
          postedAt: comment.postedAt,
          author: comment.author,
          comment: {
            ...comment,
            internal: false,
            images: comment.image ? [comment.image] : [],
            canEdit: false,
            canDelete: false,
          },
        }) satisfies AdminTicketActivityDto,
    );
  }, [comments]);

  return (
    <DocumentPaper
      theme="minimal"
      title={t("page.tickets.details.title")}
      subTitle={
        <Breadcrumbs
          pages={[
            {
              name: t("page.tickets.title"),
              to: routes.tickets.list({ slug }),
            },
            {
              name: t("page.tickets.details.title"),
            },
          ]}
        />
      }
      actions={
        ticket.status.type !== "rated" && ticket.author.id === sessionUser.id ? (
          <Button styling="secondary" onClick={rateModalOpenHandlers.setTrue} data-testid="rate-close-ticket">
            {t("page.tickets.details.actions.rate-or-close")}
          </Button>
        ) : null
      }
    >
      <div className="flex flex-col gap-4 @6xl:flex-row">
        <div className="flex max-w-6xl flex-1 flex-col gap-4">
          <div>
            <div className="flex flex-col gap-6 rounded-t-lg border-b border-grey-lightest bg-white p-4">
              {ticket.closedAt && (ticket.status.type === "rated" || ticket.status.type === "autoClosed") ? (
                <AlertBar theme="blue" icon={<Lock size={16} />}>
                  {t("page.tickets.details.info.closed-at", {
                    date: formatDate(i18n, "date", ticket.closedAt),
                  })}
                </AlertBar>
              ) : null}
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                {ticket.visibility !== "private" ? (
                  <DetailInfo title={t("page.tickets.details.info.type")}>
                    <div className="flex items-center gap-1 rounded bg-grey-lightest p-2">
                      <UsersIcon className="text-aop-basic-blue" size={16} />
                      <Capture1 className="text-blue-dark">{t("page.tickets.details.info.type.collective")}</Capture1>
                    </div>
                  </DetailInfo>
                ) : null}
                <DetailInfo title={t("page.tickets.details.info.status")}>
                  <div className="flex items-center gap-1 px-2 py-1.5">
                    <TicketStatus description={ticket.status.name} labelColor={ticket.status.color} />
                  </div>
                </DetailInfo>
                <DetailInfo title={t("page.tickets.details.info.category")}>
                  <div className="gap-1 rounded bg-grey-lightest px-2 py-1.5">
                    <Capture1>{ticket.category.name}</Capture1>
                  </div>
                </DetailInfo>
                {ticket.rating ? (
                  <DetailInfo title={t("page.tickets.details.info.rating")}>
                    <span>
                      <span
                        data-testid="rating-number"
                        className="inline-flex items-center gap-1 rounded bg-yellow-lightest px-2 py-1.5"
                      >
                        <span className="flex gap-0.5">
                          <StarIcon className="text-yellow" size={16} fill="currentColor" />
                        </span>
                        <Subtitle2>{ticket.rating ?? "-"}</Subtitle2>
                      </span>
                    </span>
                  </DetailInfo>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                <span className="shrink-0">
                  <CalendarIcon size={18} />
                </span>
                <Capture2>
                  <Trans
                    i18nKey="page.tickets.details.created-at"
                    values={{
                      date: formatDate(i18n, "datetime", ticket.postedAt),
                      name: ticket.author.fullName,
                    }}
                    components={{
                      bold: <b />,
                    }}
                  />
                </Capture2>
              </div>
            </div>
            <TicketContent ticket={ticket} />
          </div>

          <div className="rounded-lg bg-white p-4">
            <div className="flex items-center justify-between">
              <div className="flex w-full flex-col gap-2">
                <div className="flex justify-between">
                  <Headline4>{t("page.tickets.details.activities.title")}</Headline4>
                </div>
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-2">
              <div>
                <TicketResidentCommentField
                  canComment={ticket.canComment}
                  onComment={onCreateComment}
                  canRateOrReject={ticket.canComment && ticket.status.type === "closed"}
                  onReject={rejectModalOpenHandlers.setTrue}
                  onRate={rateModalOpenHandlers.setTrue}
                  statusName={ticket.status.name}
                  user={sessionUser}
                  showOtherUserCanNotCommentMessage={
                    ticket.author.id !== sessionUser.id && ticket.status.type !== "rated"
                  }
                  wasTicketCreatedInWeb={ticket.requestOrigin === "web"}
                />
                <TicketActivitiesSection
                  ticketId={ticket.id}
                  forResident
                  activities={
                    !ticket.rating && !ticket.remark
                      ? activities
                      : [
                          {
                            author: ticket.author,
                            id: "rating",
                            postedAt: ticket.closedAt || new Date().toISOString(),
                            rating: {
                              rating: ticket.rating,
                              ratingRemark: ticket.remark,
                            },
                          },
                          ...activities,
                        ]
                  }
                />
                {hasMoreComments ? (
                  <div className="p-4" ref={ref}>
                    <LoadingIcon className="inset-0 mx-auto my-4 w-6" />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
      <TicketResidentRejectModal
        title={t("page.tickets.reject-modal.title")}
        description={t("page.tickets.reject-modal.description")}
        user={sessionUser}
        isOpen={isRejectModalOpen}
        onClose={rejectModalOpenHandlers.setFalse}
        onSubmit={async (value, image) => {
          try {
            await onCreateComment({
              content: value,
              image: image,
            });
          } finally {
            rejectModalOpenHandlers.setFalse();
          }
        }}
      />
      <TicketResidentRateModal
        title={t("page.tickets.rate-modal.title")}
        description={t("page.tickets.rate-modal.description")}
        isOpen={isRateModalOpen}
        onClose={rateModalOpenHandlers.setFalse}
        isLoading={isRatingSubmitting}
        isTicketClosed={ticket.status.type === "closed"}
        openDate={ticket.postedAt}
        closingDate={ticket.closedAt}
        onSubmit={async (value) => {
          try {
            await onRate({ rating: value.rating, remark: value.remark, closingDate: value.closingDate });
          } finally {
            rateModalOpenHandlers.setFalse();
          }
        }}
      />
    </DocumentPaper>
  );
}

function DetailInfo({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <Overline1 className="text-grey-darker">{title}</Overline1>
      <div className="flex">{children}</div>
    </div>
  );
}
